/**
 * One period control for the whole application.
 *
 * A button that says which period is on screen — "Oct 2026", "Jul – Sept
 * 2026", "6 Oct 2026 · Night shift" — and opens a panel of choices, each with
 * the dates it resolves to written beside it. Somebody choosing "Last
 * quarter" sees which quarter they got without working it out; somebody who
 * needs 3 to 17 June opens "Custom date range" and types the two dates.
 *
 * Previous and Next step the period on screen by its own shape: a month to
 * the month before, a quarter to the quarter before, a shift to the shift
 * before. See `lib/period.ts` for the presets and why the financial year runs
 * April to March.
 *
 * On the reports the panel also carries the shift filter, the comparison
 * (previous period or same period last year), periods the person saved, and
 * the ones only the data knows — since the last stock count, since the last
 * close.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { Compare, Period, PeriodPreset, PeriodShift } from "../lib/period";
import {
  COMPARES,
  COMPARE_LABELS,
  PRESET_LABELS,
  PRESET_SECTIONS,
  SHIFT_PRESETS,
  describePeriod,
  describeShort,
  previousForBoard,
  resolvePeriod,
  shiftPeriod,
} from "../lib/period";
import {
  loadSaved,
  removeSaved,
  resolveSaved,
  savePeriod,
  useAnchors,
  type SavedPeriod,
} from "../lib/savedPeriods";

interface Props {
  value: Period;
  onChange: (period: Period) => void;
  /** Kept for callers that still pass it; the button is always compact. */
  compact?: boolean;
  /** The accessible name of the control. */
  label?: string;
  /** Offer the shift choices — only where figures are recorded by shift. */
  shifts?: boolean;
  /** Offer "compare with"; omitted where a screen makes no comparison. */
  compare?: Compare;
  onCompare?: (compare: Compare) => void;
  /** Offer saved and data-anchored periods (on by default). */
  saved?: boolean;
}

const SHIFT_FILTER: { value: PeriodShift | undefined; label: string }[] = [
  { value: undefined, label: "Both shifts" },
  { value: "DAY", label: "Day" },
  { value: "NIGHT", label: "Night" },
];

const same = (a: Period, b: Period) =>
  a.from === b.from && a.to === b.to && (a.shift ?? null) === (b.shift ?? null);

export function PeriodPicker({
  value,
  onChange,
  label = "Period",
  shifts = false,
  compare,
  onCompare,
  saved: offerSaved = true,
}: Props) {
  const [open, setOpen] = useState(false);
  // Which section's "Custom date range" is open, so the date boxes appear
  // under the line that was clicked rather than at the far end of the panel.
  const [custom, setCustom] = useState<string | null>(null);
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);
  const [saved, setSaved] = useState<SavedPeriod[]>(() => loadSaved());
  const [naming, setNaming] = useState<string | null>(null);
  const anchors = useAnchors(open && offerSaved);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();
  // The panel opens under the button's right edge, where the button usually
  // sits; on a narrow screen with the button at the left, that would push it
  // off the screen, so it opens under the left edge instead.
  const [alignLeft, setAlignLeft] = useState(false);

  // Resolved once per opening, so the hints and the choice agree even if the
  // panel stays open across midnight.
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!open) return;
    setNow(new Date());
    setSaved(loadSaved());

    function onPointer(e: PointerEvent) {
      if (root.current && !root.current.contains(e.target as Node)) close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close();
        button.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !button.current) return;
    const width = panel.current?.offsetWidth ?? 320;
    setAlignLeft(button.current.getBoundingClientRect().right - width < 8);
  }, [open]);

  function close() {
    setOpen(false);
    setCustom(null);
    setNaming(null);
  }

  /**
   * The shift is a filter of its own, so changing the dates keeps it — a
   * night-shift review of last month is "Last month" with Night on. Only the
   * shift presets set it, and only where this screen offers shifts at all.
   */
  function withShift(period: Period): Period {
    if (!shifts) return { ...period, shift: undefined };
    return period.shift ? period : { ...period, shift: value.shift };
  }

  function choose(preset: PeriodPreset) {
    onChange(withShift(resolvePeriod(preset, new Date(), value)));
    close();
  }

  function pick(period: Period) {
    onChange(withShift(period));
    close();
  }

  function step(direction: -1 | 1) {
    onChange(shiftPeriod(value, direction));
  }

  function apply() {
    if (!from || !to) return;
    // A range typed backwards is a slip, not a request for an empty period.
    const [a, b] = from <= to ? [from, to] : [to, from];
    onChange({ preset: "custom", from: a, to: b, shift: shifts ? value.shift : undefined });
    close();
  }

  function option(preset: PeriodPreset, key: string) {
    const resolved = withShift(resolvePeriod(preset, now, value));
    const active =
      value.preset === preset ||
      (value.preset === "custom" && same(resolved, value));
    return (
      <button
        key={key}
        type="button"
        aria-pressed={active}
        className={`periodmenu__item${active ? " is-active" : ""}`}
        onClick={() => choose(preset)}
      >
        <span>{PRESET_LABELS[preset]}</span>
        <span className="periodmenu__hint">{hint(resolved, preset)}</span>
      </button>
    );
  }

  /**
   * The dates a choice resolves to. The shift filter is shown once, above,
   * rather than on every line; only the shift choices name their shift.
   */
  function hint(period: Period, preset?: PeriodPreset): string {
    const own = preset !== undefined && SHIFT_PRESETS.includes(preset);
    return describeShort(own ? period : { ...period, shift: undefined });
  }

  function customToggle(section: string) {
    const expanded = custom === section;
    return (
      <>
        <button
          type="button"
          className="periodmenu__item periodmenu__item--custom"
          aria-expanded={expanded}
          onClick={() => {
            // Seed from what is on screen now, which Previous and Next may
            // have moved since the panel opened.
            setFrom(value.from);
            setTo(value.to);
            setCustom(expanded ? null : section);
          }}
        >
          <span>Custom date range</span>
          <span className="periodmenu__chevron" aria-hidden="true">
            {expanded ? "⌄" : "›"}
          </span>
        </button>
        {expanded && (
          <form
            className="periodmenu__custom"
            onSubmit={(e) => {
              e.preventDefault();
              apply();
            }}
          >
            <label className="inputgroup">
              <span className="label">From</span>
              <input type="date" value={from} autoFocus
                     onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label className="inputgroup">
              <span className="label">To</span>
              <input type="date" value={to}
                     onChange={(e) => setTo(e.target.value)} />
            </label>
            <button type="submit" className="btn btn--sm btn--auto"
                    disabled={!from || !to}>
              Apply
            </button>
          </form>
        )}
      </>
    );
  }

  const keep = [...saved.map((s) => ({ key: s.id, label: s.name,
                                       period: resolveSaved(s, now), saved: s })),
                ...anchors.map((a) => ({ ...a, saved: null }))];

  return (
    <div className="periodpicker" ref={root}>
      <button
        ref={button}
        type="button"
        className="periodpicker__button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`${label}: ${describePeriod(value)}`}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <svg
          className="periodpicker__icon"
          viewBox="0 0 16 16"
          width="16"
          height="16"
          aria-hidden="true"
        >
          <rect x="1.5" y="2.5" width="13" height="12" rx="1.5"
                fill="none" stroke="currentColor" />
          <path d="M1.5 6h13M5 1v3M11 1v3" stroke="currentColor" />
          <path d="M4 8.5h2M7 8.5h2M10 8.5h2M4 11.5h2M7 11.5h2"
                stroke="currentColor" strokeWidth="1.4" />
        </svg>
        <span className="periodpicker__value">{describePeriod(value)}</span>
        {compare && compare !== "prior" && (
          <span className="periodpicker__tag">{COMPARE_LABELS[compare].short}</span>
        )}
        <span className="periodpicker__caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div
          ref={panel}
          className={`periodmenu${alignLeft ? " periodmenu--left" : ""}`}
          id={panelId}
          role="dialog"
          aria-label={label}
        >
          <div className="periodmenu__steps">
            <button type="button" className="periodmenu__step"
                    onClick={() => step(-1)}>
              ‹ Previous
            </button>
            <button type="button" className="periodmenu__step"
                    onClick={() => step(1)}>
              Next ›
            </button>
          </div>

          {(shifts || onCompare) && (
            <div className="periodmenu__filters">
              {shifts && (
                <div className="periodmenu__segment" role="group" aria-label="Shift">
                  {SHIFT_FILTER.map((s) => (
                    <button
                      key={s.label}
                      type="button"
                      aria-pressed={value.shift === s.value}
                      className={value.shift === s.value ? "is-active" : undefined}
                      onClick={() => onChange({ ...value, shift: s.value })}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
              {onCompare && (
                <div className="periodmenu__segment" role="group"
                     aria-label="Compare with">
                  <span className="periodmenu__segment-label">Compare with</span>
                  {COMPARES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-pressed={compare === c}
                      className={compare === c ? "is-active" : undefined}
                      title={`${COMPARE_LABELS[c].long}: ${describePeriod(previousForBoard(value, c))}`}
                      onClick={() => onCompare(c)}
                    >
                      {COMPARE_LABELS[c].short}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {offerSaved && keep.length > 0 && (
            <div className="periodmenu__section">
              <span className="periodmenu__title">Saved</span>
              {keep.map((k) => {
                const resolved = withShift(k.period);
                const active = same(resolved, value);
                return (
                  <div key={k.key} className="periodmenu__saved">
                    <button
                      type="button"
                      aria-pressed={active}
                      className={`periodmenu__item${active ? " is-active" : ""}`}
                      onClick={() => pick(k.period)}
                    >
                      <span>{k.label}</span>
                      <span className="periodmenu__hint">{hint(resolved)}</span>
                    </button>
                    {k.saved && (
                      <button
                        type="button"
                        className="periodmenu__remove"
                        aria-label={`Remove ${k.label}`}
                        title="Remove"
                        onClick={() => setSaved(removeSaved(k.saved!.id))}
                      >
                        ×
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="periodmenu__section">
            <span className="periodmenu__title">{PRESET_SECTIONS[0].title}</span>
            {PRESET_SECTIONS[0].presets.map((p) => option(p, `rec-${p}`))}
            {customToggle("recommended")}
          </div>

          {shifts && (
            <div className="periodmenu__section">
              <span className="periodmenu__title">Shifts</span>
              {SHIFT_PRESETS.map((p) => option(p, `shift-${p}`))}
            </div>
          )}

          {PRESET_SECTIONS.slice(1).map((section) => (
            <div key={section.title} className="periodmenu__section">
              <span className="periodmenu__title">{section.title}</span>
              {section.presets.map((p) => option(p, `${section.title}-${p}`))}
              {/* Custom closes the calendar list too, as people look for it
                  in either place. */}
              {section.title === "Calendar months" && customToggle(section.title)}
            </div>
          ))}

          {offerSaved && (
            <div className="periodmenu__save">
              {naming === null ? (
                <button type="button" className="periodmenu__step"
                        onClick={() => setNaming("")}>
                  ＋ Save this period
                </button>
              ) : (
                <form
                  className="periodmenu__custom periodmenu__custom--save"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!naming.trim()) return;
                    setSaved(savePeriod(naming, value));
                    setNaming(null);
                  }}
                >
                  <label className="inputgroup">
                    <span className="label">
                      Name for {describePeriod(value)}
                      {value.preset !== "custom" &&
                        ` (stays “${PRESET_LABELS[value.preset]}”)`}
                    </span>
                    <input value={naming} autoFocus maxLength={40}
                           placeholder="e.g. Monsoon quarter"
                           onChange={(e) => setNaming(e.target.value)} />
                  </label>
                  <button type="submit" className="btn btn--sm btn--auto"
                          disabled={!naming.trim()}>
                    Save
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
